import { getCornerstone, getCornerstoneTools } from "../../utils/dicomLoader";
import { logger } from "../../utils/logger";
import { emit } from "../viewerBus";
import { registerCommand } from "./registry";

// Only measurement annotations are eligible for clearing. Segmentation contours
// (PlanarFreehandContourSegmentationTool etc.) and anything else are left alone.
const MEASUREMENT_TOOL_NAMES = new Set(["Length", "RectangleROI"]);

// Minimal shape of a Cornerstone annotation we read here. Avoids `any` while not
// depending on the full cornerstone-tools Annotation type (which the runtime
// objects don't strictly satisfy in our viewer paths).
interface CsAnnotationLike {
  annotationUID?: string;
  metadata?: { toolName?: string; referencedImageId?: string };
}

/**
 * Remove every measurement annotation matching `predicate`, then re-render.
 * Returns the number removed. Pure read of Cornerstone state — no atoms — so the
 * keyboard handler, toolbar, and context menu can all converge here.
 */
function removeMeasurements(predicate: (ann: CsAnnotationLike) => boolean): number {
  const cs = getCornerstone();
  const csTools = getCornerstoneTools();
  const all = (csTools.annotation.state.getAllAnnotations() ?? []) as CsAnnotationLike[];
  let removed = 0;
  for (const ann of all) {
    const uid = ann?.annotationUID;
    const toolName = ann?.metadata?.toolName;
    if (uid && toolName && MEASUREMENT_TOOL_NAMES.has(toolName) && predicate(ann)) {
      csTools.annotation.state.removeAnnotation(uid);
      removed++;
    }
  }
  if (removed > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cs.getRenderingEngines()?.forEach((engine: any) => engine.render());
  }
  return removed;
}

registerCommand("clearMeasurementsOnSlice", ({ imageId }) => {
  try {
    const removed = removeMeasurements((ann) => ann?.metadata?.referencedImageId === imageId);
    emit("toast", {
      message:
        removed > 0
          ? `Cleared ${removed} measurement${removed === 1 ? "" : "s"} on this slice`
          : "No measurements on this slice",
    });
    return { removed };
  } catch (e) {
    logger.warn("[clearMeasurementsOnSlice] failed:", e);
    emit("toast", { message: "Failed to clear measurements on this slice" });
    return { removed: 0 };
  }
});

registerCommand("clearAllMeasurements", () => {
  try {
    const removed = removeMeasurements(() => true);
    emit("toast", {
      message: removed > 0 ? `Cleared all measurements (${removed})` : "No measurements to clear",
    });
    return { removed };
  } catch (e) {
    logger.warn("[clearAllMeasurements] failed:", e);
    emit("toast", { message: "Failed to clear measurements" });
    return { removed: 0 };
  }
});
